import { useState } from 'react';

import { useNavigate } from 'react-router-dom';
import { useGetSavedTemplatesQuery, SavedDesign } from '../../../features/templates/templatesApi';
import { TemplateData } from '../../../types/design';
import { Clock, FileText } from 'lucide-react';
import TemplateEditorPage from '../../template-editor/TemplateEditorPage';

const AddIcon = () => (
  <div className="absolute contents inset-0" data-name="vuesax/linear/add">
    <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 24 24">
      <g id="add">
        <path d="M6 12H18" id="Vector" stroke="var(--stroke-0, #292D32)" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" />
        <path d="M12 18V6" id="Vector_2" stroke="var(--stroke-0, #292D32)" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" />
      </g>
    </svg>
  </div>
);

const CreateNewDesignCard = ({ onClick }: { onClick: () => void }) => (
  <div 
    className="bg-gray-50 h-[137px] relative rounded-[8px] shrink-0 w-[230px] cursor-pointer hover:bg-gray-100 transition-colors group"
    onClick={onClick}
  >
    <div className="overflow-clip relative rounded-[inherit] size-full">
      <div className="absolute h-[137px] left-0 top-0 w-full" data-name="image 1" />
      <div className="absolute content-stretch flex flex-col gap-[8px] items-center left-[calc(50%+0.5px)] top-[calc(50%-0.5px)] translate-x-[-50%] translate-y-[-50%] w-[131px]">
        <div className="bg-[#f0f2f5] overflow-clip relative rounded-[4px] shrink-0 size-[36px] group-hover:bg-[#e4e7ec] transition-colors">
          <div className="absolute left-[6px] size-[24px] top-[6px]" data-name="add">
            <AddIcon />
          </div>
        </div>
        <div className="flex flex-col font-sans font-normal justify-center leading-[0] min-w-full relative shrink-0 text-[#1d2739] text-[15px] w-[min-content]" style={{ fontVariationSettings: "'opsz' 14, 'wdth' 100" }}>
          <p className="leading-[normal]">Create new design</p>
        </div>
      </div>
    </div>
    <div aria-hidden="true" className="absolute border border-[#e4e7ec] border-dashed inset-0 pointer-events-none rounded-[8px] group-hover:border-[#30a84b] transition-colors" />
  </div>
);

const DesignCard = ({ design, onClick }: { design: SavedDesign, onClick: () => void }) => {
  const date = new Date(design.updatedAt).toLocaleDateString();
  const time = new Date(design.updatedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  return (
    <div
      className="bg-white border border-[#e4e7ec] h-[137px] relative rounded-[8px] shrink-0 w-[230px] cursor-pointer hover:shadow-md transition-all group overflow-hidden"
      onClick={onClick}
    >
      <div className="absolute inset-0 bg-gray-50 flex items-center justify-center">
        {design.thumbnail ? (
          <img
            src={design.thumbnail}
            alt={design.name}
            className="size-full object-cover group-hover:scale-110 transition-transform duration-500"
          />
        ) : (
          <div className="opacity-10 group-hover:scale-110 transition-transform duration-500">
            <FileText size={80} />
          </div>
        )}
      </div>

      <div className="absolute bottom-0 inset-x-0 p-3 bg-gradient-to-t from-black/60 to-transparent text-white">
        <div className="font-bold text-[13px] truncate">{design.name || 'Untitled Design'}</div>
        <div className="flex items-center gap-1 text-[10px] opacity-80">
          <Clock size={10} />
          <span>Last edited {date} at {time}</span>
        </div>
      </div>

      <div className="absolute top-2 right-2 bg-[#30A84B] text-white text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider opacity-0 group-hover:opacity-100 transition-opacity">
        Open Latest
      </div>
    </div>
  );
};

const RecentDesignsHeader = ({ onViewAll }: { onViewAll: () => void }) => (
  <div className="content-stretch flex w-full justify-between items-center mb-1">
    <div className="content-stretch flex flex-col gap-[4px] items-start leading-[0] relative shrink-0">
      <div className="flex flex-col font-sans font-bold justify-center min-w-full relative shrink-0 text-[#1d2739] text-[18px] w-[min-content]" style={{ fontVariationSettings: "'opsz' 14, 'wdth' 100" }}>
        <p className="leading-[normal]">Your Recent Designs</p>
      </div>
      <div className="flex flex-col font-sans font-normal justify-center relative shrink-0 text-[#667185] text-[13px] text-nowrap" style={{ fontVariationSettings: "'opsz' 14, 'wdth' 100" }}>
        <p className="leading-[normal] whitespace-pre">The latest version of your templates is always ready for you.</p>
      </div>
    </div>
    <p
      className="font-sans font-medium leading-[24px] relative shrink-0 text-[#227735] text-[13px] text-nowrap whitespace-pre cursor-pointer hover:underline"
      style={{ fontVariationSettings: "'opsz' 14, 'wdth' 100" }}
      onClick={onViewAll}
    >
      View All
    </p>
  </div>
);

import { CustomSizeModal } from './CustomSizeModal';

export const RecentDesigns = () => {
  const navigate = useNavigate();
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateData | null>(null);
  const [isCustomSizeModalOpen, setIsCustomSizeModalOpen] = useState(false);

  const { data: savedData, isLoading } = useGetSavedTemplatesQuery({
    page: 1,
    limit: 5
  });

  const handleDesignClick = (design: SavedDesign) => {
    // Unpack data from the saved design
    const { id: originId, ...designData } = design.data || {};

    setSelectedTemplate({
      id: design._id,
      originTemplateId: originId,
      name: design.name || 'Saved Design',
      previewUrl: design.thumbnail || '',
      isCustom: true,
      ...designData
    });
  };

  const handleViewAll = () => {
    navigate('/my-projects');
  };

  const handleCreateNewDesign = () => {
    setIsCustomSizeModalOpen(true);
  };

  if (isLoading) {
    return (
      <div className="content-stretch flex flex-col gap-[16px] items-start relative shrink-0 w-full animate-pulse">
        <div className="h-10 w-48 bg-gray-200 rounded mb-4" />
        <div className="content-stretch flex flex-nowrap gap-[16px] items-center relative shrink-0 w-full overflow-x-auto pb-2">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-[137px] bg-gray-100 rounded-[8px] shrink-0 w-[230px]" />
          ))}
        </div>
      </div>
    );
  }

  const designs = (savedData?.data || []).filter(d => d.category !== 'Brand Kit');

  return (
    <div className="content-stretch flex flex-col gap-[16px] items-start relative shrink-0 w-full">
      <RecentDesignsHeader onViewAll={handleViewAll} />
      <div className="content-stretch flex flex-nowrap gap-[16px] items-center relative shrink-0 w-full overflow-x-auto pb-2 scrollbar-hide">
        <CreateNewDesignCard onClick={handleCreateNewDesign} />
        {designs.map((design) => (
          <DesignCard
            key={design._id}
            design={design}
            onClick={() => handleDesignClick(design)}
          />
        ))}
      </div>

      {selectedTemplate && (
        <div className="fixed inset-0 z-[9999] bg-white overflow-auto">
          <TemplateEditorPage
            initialTemplate={selectedTemplate}
            onBack={() => setSelectedTemplate(null)}
          />
        </div>
      )}

      {/* Flexible Size Selection Modal */}
      <CustomSizeModal
        isOpen={isCustomSizeModalOpen}
        onClose={() => setIsCustomSizeModalOpen(false)}
      />
    </div>
  );
};

